/* ════════════════════════════════════════════════════════════════════════════
   StudoX Flow — CONTENT EXPANSION ENGINE
   ----------------------------------------------------------------------------
   Turns the hand-authored BANKS + procedural math/logic generators into
   hundreds of playable, age-banded modules. Everything is DETERMINISTIC:
   a seeded PRNG (module-id → seed) means every kid sees the same task set for
   a given module — results stay comparable and replays stay fair.

   Bands: 1 = 8–9 · 2 = 10–11 · 3 = 12–13 · 4 = 14–16
   ════════════════════════════════════════════════════════════════════════════ */
import { BANKS } from "./banks";
import { BANKS2 } from "./banks2";

/* Drop 1 + Drop 2 merged per world. Drop-2 entries append AFTER the drop-1
   list, so all existing module ids stay stable (no lost progress). */
const ALL_BANKS = {};
for (const src of [BANKS, BANKS2]) {
  for (const [world, entries] of Object.entries(src)) {
    (ALL_BANKS[world] = ALL_BANKS[world] || []).push(...entries);
  }
}

/* ── deterministic PRNG (mulberry32) ─────────────────────────────────────── */
function rngFromString(str) {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  let a = h >>> 0;
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const ri = (rnd, min, max) => Math.floor(rnd() * (max - min + 1)) + min;
const pickOther = (rnd, correct, spread) => {
  let v;
  do { v = correct + ri(rnd, -spread, spread); } while (v === correct || v < 0);
  return v;
};
/** Build 4 unique options, correct first. */
function opts(rnd, correct, spread) {
  const set = new Set([correct]);
  let guard = 0;
  while (set.size < 4 && guard++ < 60) set.add(pickOther(rnd, correct, Math.max(2, spread)));
  return [...set].map(String);
}

const BAND_META = {
  1: { age: "8–9",   grade: "3.–4. Klasse",  diff: 1 },
  2: { age: "10–11", grade: "4.–6. Klasse",  diff: 1 },
  3: { age: "12–13", grade: "6.–8. Klasse",  diff: 2 },
  4: { age: "14–16", grade: "8.–10. Klasse", diff: 3 },
};

/* ═══ MATHE — question factories per band ════════════════════════════════ */
const MATH_FACTORIES = {
  1: [
    (r) => { const a = ri(r, 5, 60), b = ri(r, 5, 39); return { q: `${a} + ${b} = ?`, c: a + b, s: 8 }; },
    (r) => { const a = ri(r, 30, 99), b = ri(r, 5, a - 5); return { q: `${a} − ${b} = ?`, c: a - b, s: 8 }; },
    (r) => { const a = ri(r, 2, 10), b = ri(r, 2, 10); return { q: `${a} × ${b} = ?`, c: a * b, s: 6 }; },
    (r) => { const b = ri(r, 2, 10), c = ri(r, 2, 10); return { q: `${b * c} ÷ ${b} = ?`, c, s: 3 }; },
    (r) => { const a = ri(r, 6, 50) * 2; return { q: `Die Hälfte von ${a}?`, c: a / 2, s: 5 }; },
    (r) => { const a = ri(r, 6, 49); return { q: `Das Doppelte von ${a}?`, c: a * 2, s: 6 }; },
    (r) => { const a = ri(r, 3, 9), b = ri(r, 2, 6); return { q: `${a} Packungen mit je ${b} Stickern. Wie viele Sticker?`, c: a * b, s: 5 }; },
    (r) => { const a = ri(r, 20, 90); const b = Math.round(a / 10) * 10; return { q: `Runde ${a} auf den nächsten Zehner.`, c: b, s: 10 }; },
  ],
  2: [
    (r) => { const a = ri(r, 120, 800), b = ri(r, 50, 199); return { q: `${a} + ${b} = ?`, c: a + b, s: 30 }; },
    (r) => { const a = ri(r, 300, 999), b = ri(r, 50, 280); return { q: `${a} − ${b} = ?`, c: a - b, s: 30 }; },
    (r) => { const a = ri(r, 11, 25), b = ri(r, 3, 9); return { q: `${a} × ${b} = ?`, c: a * b, s: 12 }; },
    (r) => { const b = ri(r, 3, 12), c = ri(r, 11, 30); return { q: `${b * c} ÷ ${b} = ?`, c, s: 5 }; },
    (r) => { const h = ri(r, 1, 3), m = ri(r, 1, 5) * 10; return { q: `${h} Stunde(n) und ${m} Minuten — wie viele Minuten gesamt?`, c: h * 60 + m, s: 15 }; },
    (r) => { const p = ri(r, 2, 9), e = ri(r, 2, 5); return { q: `Ein Heft kostet ${p} €. Was kosten ${e} Hefte?`, c: p * e, s: 6 }; },
    (r) => { const a = ri(r, 100, 950); return { q: `Runde ${a} auf den nächsten Hunderter.`, c: Math.round(a / 100) * 100, s: 100 }; },
    (r) => { const a = ri(r, 4, 12), b = ri(r, 4, 12); return { q: `Ein Rechteck ist ${a} cm lang und ${b} cm breit. Umfang?`, c: 2 * (a + b), s: 8 }; },
  ],
  3: [
    (r) => { const b = ri(r, 2, 9) * 10, p = [10, 20, 25, 50][ri(r, 0, 3)]; return { q: `${p} % von ${b} = ?`, c: (b * p) / 100, s: 8 }; },
    (r) => { const a = ri(r, 3, 12), b = ri(r, 3, 12); return { q: `(−${a}) + ${b} = ?`, c: b - a, s: 5, neg: true }; },
    (r) => { const a = ri(r, 2, 12), b = ri(r, 2, 9); return { q: `(−${a}) × ${b} = ?`, c: -(a * b), s: 10, neg: true }; },
    (r) => { const n = ri(r, 2, 6); return { q: `${n}³ = ?`, c: n * n * n, s: 15 }; },
    (r) => { const n = ri(r, 4, 15); return { q: `${n}² = ?`, c: n * n, s: 12 }; },
    (r) => { const a = ri(r, 6, 15), b = ri(r, 2, 5); return { q: `Ein Rechteck: ${a} m × ${b} m. Fläche in m²?`, c: a * b, s: 8 }; },
    (r) => { const k = ri(r, 30, 90), p = [10, 20, 25, 50][ri(r, 0, 3)]; return { q: `${k} € mit ${p} % Rabatt. Neuer Preis in €?`, c: k - (k * p) / 100, s: 6 }; },
    (r) => { const a = ri(r, 2, 9), x = ri(r, 3, 12); return { q: `Löse: x + ${a} = ${a + x}. x = ?`, c: x, s: 4 }; },
  ],
  4: [
    (r) => { const a = ri(r, 2, 9), x = ri(r, 3, 12); return { q: `Löse: ${a}·x = ${a * x}. x = ?`, c: x, s: 4 }; },
    (r) => { const a = ri(r, 2, 6), b = ri(r, 3, 20), x = ri(r, 2, 10); return { q: `Löse: ${a}x + ${b} = ${a * x + b}. x = ?`, c: x, s: 4 }; },
    (r) => { const k = ri(r, 200, 900), p = ri(r, 2, 8); return { q: `${k} € zu ${p} % Zinsen. Zinsen nach 1 Jahr in €?`, c: (k * p) / 100, s: 10 }; },
    (r) => { const a = ri(r, 3, 9); return { q: `Ein Würfel hat Kantenlänge ${a} cm. Volumen in cm³?`, c: a ** 3, s: 20 }; },
    (r) => { const km = ri(r, 12, 40) * 10, h = [2, 4, 5][ri(r, 0, 2)]; return { q: `${km} km in ${h} Stunden. Durchschnitt in km/h?`, c: km / h, s: 10 }; },
    (r) => { const b = ri(r, 4, 20) * 10, p = ri(r, 10, 40); return { q: `Preis steigt von ${b} € um ${p} %. Neuer Preis in €?`, c: b + (b * p) / 100, s: 12 }; },
    (r) => { const a = ri(r, 2, 5), b = ri(r, 6, 12); return { q: `Verhältnis ${a}:${b} — wie viel bei ${a * 3}:x? x = ?`, c: b * 3, s: 6 }; },
    (r) => { const n = ri(r, 11, 20); return { q: `${n}² = ?`, c: n * n, s: 25 }; },
  ],
};

const MATH_TITLES = {
  1: ["Zahlen-Start", "Rechen-Rakete", "Plus-Minus-Park", "Mal-Nehmen-Meister", "Teilen-Turbo", "Zahlen-Zoo", "Rechen-Ritter", "Kopfrechen-Kids"],
  2: ["Tausender-Tour", "Rechen-Werkstatt", "Zeit & Geld", "Mal-Marathon", "Runden-Rennen", "Umfang-Universum", "Zahlen-Dschungel", "Rechen-Liga"],
  3: ["Prozent-Planet", "Minus-Welt", "Potenz-Portal", "Flächen-Forscher", "Rabatt-Radar", "Gleichungs-Gym", "Quadrat-Quest", "Zahlen-Nebel"],
  4: ["Gleichungs-Arena", "Zins-Zentrale", "Volumen-Vault", "Tempo-Trigonauten", "Prozent-Profi-Liga", "Verhältnis-Villa", "Algebra-Alpen", "Formel-Fabrik"],
};

function buildMathModules() {
  const out = [];
  for (const band of [1, 2, 3, 4]) {
    const meta = BAND_META[band];
    const facs = MATH_FACTORIES[band];
    /* 8 quiz modules per band */
    MATH_TITLES[band].forEach((title, mi) => {
      const id = `mathe-b${band}-q${mi}`;
      const rnd = rngFromString(id);
      const questions = Array.from({ length: 8 }, (_, qi) => {
        const f = facs[(mi + qi) % facs.length](rnd);
        return { q: f.q, options: f.neg ? negOpts(rnd, f.c, f.s) : opts(rnd, f.c, f.s), answer: 0, explain: `Richtig: ${f.c}.` };
      });
      out.push({
        id, world: "mathe", title, subtitle: `Mathe · Stufe ${band}`,
        grade: meta.grade, age: meta.age, difficulty: meta.diff, mode: "quiz",
        xp: 50 + band * 10, coins: 10 + band * 2, badge: "🪐 Zahlen-Pilot",
        questions,
      });
    });
    /* 3 tempo modules per band */
    for (let ti = 0; ti < 3; ti++) {
      const id = `mathe-b${band}-t${ti}`;
      const rnd = rngFromString(id);
      const questions = Array.from({ length: 10 }, (_, qi) => {
        const f = facs[(ti * 3 + qi) % facs.length](rnd);
        return { q: f.q, options: f.neg ? negOpts(rnd, f.c, f.s) : opts(rnd, f.c, f.s), answer: 0 };
      });
      out.push({
        id, world: "mathe", title: `Blitz-Mathe ${["I", "II", "III"][ti]}`, subtitle: `Tempo · Stufe ${band}`,
        grade: meta.grade, age: meta.age, difficulty: Math.min(3, meta.diff + 1), mode: "tempo",
        xp: 60 + band * 10, coins: 12 + band * 2, badge: "⏱️ Blitzhirn",
        questions,
      });
    }
  }
  return out;
}
/* options for possibly-negative answers */
function negOpts(rnd, correct, spread) {
  const set = new Set([correct]);
  let guard = 0;
  while (set.size < 4 && guard++ < 60) {
    const v = correct + ri(rnd, -Math.max(2, spread), Math.max(2, spread));
    if (v !== correct) set.add(v);
  }
  return [...set].map(String);
}

/* ═══ DENKEN — number-sequence generators per band ═══════════════════════ */
function buildSequenceModules() {
  const out = [];
  const makers = {
    1: [(r) => seq(r, ri(r, 1, 9), (v) => v + ri(r, 2, 5)), (r) => seq(r, ri(r, 20, 60), (v) => v - ri(r, 2, 4))],
    2: [(r) => seq(r, ri(r, 2, 6), (v) => v * 2), (r) => seq(r, ri(r, 1, 6), (v) => v + ri(r, 4, 9))],
    3: [(r) => seq(r, ri(r, 2, 4), (v) => v * 3), (r) => altSeq(r), (r) => seq(r, ri(r, 1, 5), (v) => v * 2 + 1)],
    4: [(r) => sqSeq(r), (r) => fibLike(r), (r) => seq(r, ri(r, 2, 5), (v) => v * v > 500 ? v + 7 : v * 2 - 1)],
  };
  function seq(r, start, step) {
    const vals = [start];
    for (let i = 0; i < 4; i++) vals.push(step(vals[vals.length - 1]));
    return vals;
  }
  function altSeq(r) { /* +a, −b alternating */
    const a = ri(r, 4, 9), b = ri(r, 1, 3);
    const vals = [ri(r, 2, 10)];
    for (let i = 0; i < 4; i++) vals.push(i % 2 === 0 ? vals[vals.length - 1] + a : vals[vals.length - 1] - b);
    return vals;
  }
  function sqSeq(r) { const s = ri(r, 1, 4); return [s, s + 1, s + 2, s + 3, s + 4].map((n) => n * n); }
  function fibLike(r) {
    const vals = [ri(r, 1, 4), ri(r, 2, 5)];
    for (let i = 0; i < 3; i++) vals.push(vals[vals.length - 1] + vals[vals.length - 2]);
    return vals;
  }
  for (const band of [1, 2, 3, 4]) {
    const meta = BAND_META[band];
    for (let mi = 0; mi < 3; mi++) {
      const id = `denken-b${band}-s${mi}`;
      const rnd = rngFromString(id);
      const questions = Array.from({ length: 8 }, (_, qi) => {
        const vals = makers[band][(mi + qi) % makers[band].length](rnd);
        const answer = vals[vals.length - 1];
        const shown = vals.slice(0, -1).join(", ");
        return { q: `Wie geht die Folge weiter? ${shown}, …`, options: opts(rnd, answer, Math.max(3, Math.round(Math.abs(answer) * 0.3))), answer: 0, explain: `Die Folge ergibt ${answer}.` };
      });
      out.push({
        id, world: "denken", title: `Muster-Matrix ${["α", "β", "γ"][mi]}`, subtitle: `Zahlenfolgen · Stufe ${band}`,
        grade: meta.grade, age: meta.age, difficulty: meta.diff, mode: "quiz",
        xp: 55 + band * 10, coins: 11 + band * 2, badge: "🧩 Muster-Magier",
        questions,
      });
    }
  }
  return out;
}

/* ═══ BANKS → modules (chunked) ══════════════════════════════════════════ */
const CHUNK = { quiz: 6, cloze: 6, pairs: 6, memory: 6 };

function buildBankModules() {
  const out = [];
  for (const [world, banks] of Object.entries(ALL_BANKS)) {
    banks.forEach((bank, bi) => {
      const meta = BAND_META[bank.band];
      const size = CHUNK[bank.mode] || 6;
      const chunks = [];
      for (let i = 0; i < bank.items.length; i += size) {
        const slice = bank.items.slice(i, i + size);
        if (slice.length >= 4) chunks.push(slice);
      }
      chunks.forEach((slice, ci) => {
        const id = `${world}-b${bank.band}-x${bi}-${ci}`;
        const base = {
          id, world,
          title: chunks.length > 1 ? `${bank.theme} ${["I", "II", "III", "IV"][ci] || ci + 1}` : bank.theme,
          subtitle: `${bank.theme} · Stufe ${bank.band}`,
          grade: meta.grade, age: meta.age, difficulty: meta.diff,
          xp: 50 + bank.band * 10, coins: 10 + bank.band * 2,
          badge: "🌟 Wissens-Sammler",
        };
        if (bank.mode === "quiz") {
          out.push({ ...base, mode: "quiz",
            questions: slice.map(([q, c, w1, w2, w3, explain]) => ({
              q, options: [c, w1, w2, w3].filter(Boolean), answer: 0, explain: explain || `Richtig: ${c}`,
            })) });
        } else if (bank.mode === "cloze") {
          out.push({ ...base, mode: "cloze", prompt: bank.prompt,
            items: slice.map(([text, answer, ...wrong]) => ({ text, answer, options: [answer, ...wrong] })) });
        } else { /* pairs → alternate match & memory for variety */
          out.push({ ...base, mode: ci % 2 === 0 ? "match" : "memory", prompt: bank.prompt,
            pairs: slice.map(([a, b]) => ({ a, b })) });
        }
      });
    });
  }
  return out;
}

/* ═══ export ═════════════════════════════════════════════════════════════ */
export const EXPANSION_MODULES = [
  ...buildMathModules(),
  ...buildSequenceModules(),
  ...buildBankModules(),
];
